import { PRACTICE_ATTEMPT_ERROR_CODE, PRACTICE_ATTEMPT_STATUS } from '../../../types/practiceProposal';

import type { PracticeProposal } from '../../../types/practiceProposal';

/** What the card says about the proposal; each one maps to a single copy in the card hook. */
export type PracticeProposalCardStatus = 'not_started' | 'creating' | 'active' | 'completed' | 'discarded' | 'failed' | 'unavailable';

/**
 * What a card button does. Every action opens the launcher; only `again` asks for a new attempt.
 * `summary` and `continue` resume the current attempt, which the launcher resolves to its summary
 * or its first unanswered activity.
 */
export type PracticeProposalCardAction = 'start' | 'continue' | 'summary' | 'again' | 'retry';

export type PracticeProposalCardState = {
  answered: number | null;
  maxActivityNumber: number | null;
  primaryAction: PracticeProposalCardAction | null;
  secondaryAction: PracticeProposalCardAction | null;
  status: PracticeProposalCardStatus;
};

const NO_PROGRESS = { answered: null, maxActivityNumber: null };

/**
 * Resolves the card from the proposal the backend returns (§4.10 and §4.11 of the spec). A
 * practice that is no longer available offers no way to start it, but a completed one keeps
 * «Ver resumen»: its results still exist.
 */
export function resolvePracticeProposalCardState(proposal: PracticeProposal): PracticeProposalCardState {
  const attempt = proposal.attempt ?? null;
  const isCompleted = attempt?.status === PRACTICE_ATTEMPT_STATUS.COMPLETED;
  const isUnavailable =
    !proposal.available || (attempt?.status === PRACTICE_ATTEMPT_STATUS.FAILED && attempt.errorCode === PRACTICE_ATTEMPT_ERROR_CODE.PRACTICE_UNAVAILABLE);

  if (isUnavailable) {
    return { ...NO_PROGRESS, primaryAction: isCompleted ? 'summary' : null, secondaryAction: null, status: 'unavailable' };
  }

  switch (attempt?.status) {
    case undefined:
      return { ...NO_PROGRESS, primaryAction: 'start', secondaryAction: null, status: 'not_started' };
    case PRACTICE_ATTEMPT_STATUS.CREATING:
      return { ...NO_PROGRESS, primaryAction: null, secondaryAction: null, status: 'creating' };
    case PRACTICE_ATTEMPT_STATUS.ACTIVE:
      return {
        answered: attempt.answered ?? null,
        maxActivityNumber: attempt.maxActivityNumber ?? null,
        primaryAction: 'continue',
        secondaryAction: null,
        status: 'active',
      };
    case PRACTICE_ATTEMPT_STATUS.COMPLETED:
      return { ...NO_PROGRESS, primaryAction: 'summary', secondaryAction: 'again', status: 'completed' };
    case PRACTICE_ATTEMPT_STATUS.DISCARDED:
      return { ...NO_PROGRESS, primaryAction: 'again', secondaryAction: null, status: 'discarded' };
    default:
      // FAILED with CREATION_FAILED, or with a code this version does not know yet: retrying is
      // the only safe offer, and the backend decides whether the retry can go ahead.
      return { ...NO_PROGRESS, primaryAction: 'retry', secondaryAction: null, status: 'failed' };
  }
}
